import React from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { ArrowLeft, ArrowRight } from "lucide-react";

import UIDatePicker from "../UIDatePicker";
import { clx } from "../../../utils/Helpers";
import "./UIDateRangeFields.scss";

/**
 * Two `UIDatePicker`s laid out as a start/end range, with the "end date can't be
 * before start date" rule wired in by default (`enforceOrder`):
 * - Blocks the invalid side of the calendar via `minDate`/`maxDate`.
 * - Falls back to an `error`/`helperText` state for values that are already
 *   invalid (e.g. loaded from stale data) — pass `rangeErrorMessage` to show it.
 *
 * Explicit `startError`/`endError`/`startHelperText`/`endHelperText` always win over
 * the built-in check, so a caller running its own Formik/Yup validation (cross-field
 * rules beyond simple ordering) can pass `enforceOrder={false}` and drive both fully.
 *
 * `pickerOnly` (default true) blocks free-text typing — the field only accepts a
 * value chosen from the calendar — and shows a clear (X) button once a date is set.
 */
const UIDateRangeFields = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startLabel,
  endLabel,
  startPlaceholder,
  endPlaceholder,
  startName,
  endName,
  enforceOrder,
  pickerOnly,
  rangeErrorMessage,
  startError,
  endError,
  startHelperText,
  endHelperText,
  hideSeparator,
  startProps,
  endProps,
  className,
  classNames,
}) => {
  const lang = useSelector((state) => state.locale.lang);

  const isRangeInvalid =
    enforceOrder &&
    !!startDate &&
    !!endDate &&
    dayjs(endDate).isBefore(dayjs(startDate), "day");

  return (
    <div className={clx("ui-date-range-fields", className)}>
      <UIDatePicker
        name={startName}
        label={startLabel}
        placeholder={startPlaceholder}
        value={startDate}
        onChange={onStartDateChange}
        maxDate={enforceOrder ? endDate || undefined : undefined}
        preventManualDateEntry={pickerOnly}
        clearable={pickerOnly}
        error={startError}
        helperText={startHelperText}
        classNames={{
          root: clx("ui-date-range-fields__field", classNames?.start),
        }}
        {...startProps}
      />
      {!hideSeparator && (
        <span className="ui-date-range-fields__separator" aria-hidden="true">
          {lang === "ar" ? <ArrowLeft size={20} /> : <ArrowRight size={20} />}
        </span>
      )}
      <UIDatePicker
        name={endName}
        label={endLabel}
        placeholder={endPlaceholder}
        value={endDate}
        onChange={onEndDateChange}
        minDate={enforceOrder ? startDate || undefined : undefined}
        preventManualDateEntry={pickerOnly}
        clearable={pickerOnly}
        error={endError ?? isRangeInvalid}
        helperText={endHelperText ?? (isRangeInvalid ? rangeErrorMessage : undefined)}
        classNames={{
          root: clx("ui-date-range-fields__field", classNames?.end),
        }}
        {...endProps}
      />
    </div>
  );
};

UIDateRangeFields.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onStartDateChange: PropTypes.func,
  onEndDateChange: PropTypes.func,
  startLabel: PropTypes.node,
  endLabel: PropTypes.node,
  startPlaceholder: PropTypes.string,
  endPlaceholder: PropTypes.string,
  startName: PropTypes.string,
  endName: PropTypes.string,
  /** Blocks picking an end date before the start date (and vice versa). Set false to hand full control to your own validation. */
  enforceOrder: PropTypes.bool,
  /** Blocks free-text typing (calendar-only selection) and shows a clear button once a date is set. */
  pickerOnly: PropTypes.bool,
  rangeErrorMessage: PropTypes.node,
  startError: PropTypes.bool,
  endError: PropTypes.bool,
  startHelperText: PropTypes.node,
  endHelperText: PropTypes.node,
  hideSeparator: PropTypes.bool,
  /** Extra props passed straight through to the start `UIDatePicker` (e.g. `disablePast`). */
  startProps: PropTypes.object,
  /** Extra props passed straight through to the end `UIDatePicker`. */
  endProps: PropTypes.object,
  className: PropTypes.string,
  classNames: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string,
  }),
};

UIDateRangeFields.defaultProps = {
  startDate: "",
  endDate: "",
  onStartDateChange: undefined,
  onEndDateChange: undefined,
  startLabel: null,
  endLabel: null,
  startPlaceholder: undefined,
  endPlaceholder: undefined,
  startName: "startDate",
  endName: "endDate",
  enforceOrder: true,
  pickerOnly: true,
  rangeErrorMessage: undefined,
  startError: undefined,
  endError: undefined,
  startHelperText: undefined,
  endHelperText: undefined,
  hideSeparator: false,
  startProps: {},
  endProps: {},
  className: undefined,
  classNames: {},
};

export default UIDateRangeFields;
